import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { apiRequest } from "./queryClient";
import type { PublicUser } from "@shared/schema";

interface AuthContextType {
  user: PublicUser | null;
  token: string | null;
  isLoading: boolean;
  login: (email: string, password: string, remember?: boolean) => Promise<void>;
  register: (name: string, email: string, password: string, ref?: string, remember?: boolean, promo?: string) => Promise<any>;
  verifyRegister: (email: string, code: string, remember?: boolean) => Promise<any>;
  // Eugene 2026-05-15: для SMS-OTP / Telegram-login и других channel'ов
  // которые сами создают session token. Принимает token, дёргает /api/me
  // чтобы получить user info, сохраняет в state и cookie.
  loginByToken: (token: string, remember?: boolean) => Promise<PublicUser | null>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Cookie helpers. setCookie УДАЛЁН (блок 2, HttpOnly Phase A): auth-cookie
// ставит только сервер; getCookie/deleteCookie остаются для legacy-совместимости.
function getCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return m ? m[1] : null;
}
function deleteCookie(name: string) {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
}

// Initialize token from cookie BEFORE any component renders
let globalToken: string | null = getCookie("auth_token");

export function getAuthToken(): string | null {
  return globalToken;
}

// Patch fetch to include auth header
const originalFetch = window.fetch;
window.fetch = async function(input, init) {
  if (globalToken && typeof input === 'string' && (input.includes('/api/') || input.includes('port/5000/api/'))) {
    const headers = new Headers(init?.headers);
    if (!headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${globalToken}`);
    }
    return originalFetch.call(this, input, { ...init, headers });
  }
  return originalFetch.call(this, input, init);
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const setAuthState = useCallback((t: string | null, u: PublicUser | null, _remember = true) => {
    globalToken = t;
    setToken(t);
    setUser(u);
    // Приёмка блок 2 (HttpOnly, Phase A): cookie теперь ставит СЕРВЕР
    // (Set-Cookie HttpOnly на login/register/verify) — JS-запись убрана,
    // XSS больше не читает токен из document.cookie. In-memory globalToken
    // живёт до перезагрузки (Bearer), дальше auth несёт HttpOnly-cookie.
    // deleteCookie — только зачистка СТАРОЙ (не-HttpOnly) cookie при logout;
    // HttpOnly гасит сервер (clearAuthCookie в /api/auth/logout).
    // auth_hint — НЕчувствительный флаг «залогинен» (не токен): нужен на boot,
    // т.к. HttpOnly-cookie из JS не видна, а дёргать /api/auth/me у анонимов
    // на каждой загрузке главной — лишний трафик.
    if (t) {
      try { localStorage.setItem("auth_hint", "1"); } catch {}
    } else {
      deleteCookie("auth_token");
      try { localStorage.removeItem("auth_hint"); } catch {}
    }
  }, []);

  const refreshUser = useCallback(async () => {
    if (!globalToken) return;
    try {
      const res = await apiRequest("GET", "/api/auth/me");
      const data = await res.json();
      setUser(data);
    } catch {
      setAuthState(null, null);
    }
  }, [setAuthState]);

  // Restore session on page load. Блок 2 (HttpOnly): токен может быть
  // НЕчитаемым из JS (HttpOnly-cookie) — поэтому /api/auth/me зовём ВСЕГДА:
  // браузер сам приложит cookie (same-origin). Старая читаемая cookie
  // (до миграции) продолжает работать — если есть, кладём в globalToken (Bearer).
  useEffect(() => {
    const savedToken = getCookie("auth_token"); // legacy не-HttpOnly (совместимость)
    let hint = false;
    try { hint = localStorage.getItem("auth_hint") === "1"; } catch {}
    if (!savedToken && !hint) { setIsLoading(false); return; } // аноним — без лишнего запроса
    if (savedToken) {
      globalToken = savedToken;
      setToken(savedToken);
    }
    apiRequest("GET", "/api/auth/me")
      .then(res => res.json())
      .then(data => {
        if (data && data.id) {
          setUser(data);
        } else if (savedToken) {
          deleteCookie("auth_token");
          globalToken = null;
          setToken(null);
        }
      })
      .catch(() => {
        if (savedToken) {
          deleteCookie("auth_token");
          globalToken = null;
          setToken(null);
        }
      })
      .finally(() => setIsLoading(false));
  }, []);

  // Listen for auth:reset event dispatched by forgot-password page after successful reset
  useEffect(() => {
    const handler = (e: Event) => {
      const { token, user } = (e as CustomEvent).detail;
      if (token && user) {
        setAuthState(token, user);
      }
    };
    window.addEventListener("auth:reset", handler);
    return () => window.removeEventListener("auth:reset", handler);
  }, [setAuthState]);

  const login = useCallback(async (email: string, password: string, remember = true) => {
    const res = await apiRequest("POST", "/api/auth/login", { email, password });
    const data = await res.json();
    setAuthState(data.token, data.user, remember);
  }, [setAuthState]);

  const register = useCallback(async (name: string, email: string, password: string, ref?: string, remember = true, promo?: string) => {
    const res = await apiRequest("POST", "/api/auth/register", { name, email, password, ref, promo });
    const data = await res.json();
    if (data.waitlist) return data;        // платформа на тестировании — waitlist
    if (data.needVerification) return data; // need email code
    setAuthState(data.token, data.user, remember);
    return data;
  }, [setAuthState]);

  const verifyRegister = useCallback(async (email: string, code: string, remember = true) => {
    const res = await apiRequest("POST", "/api/auth/verify-register", { email, code });
    const data = await res.json();
    setAuthState(data.token, data.user, remember);
    return data;
  }, [setAuthState]);

  // Eugene 2026-05-15: универсальный «у меня уже есть token, залогинь».
  // Используется SMS-OTP, Telegram-login, change_phone flow.
  //
  // Eugene 2026-05-16: критический фикс race condition «вы авторизованы
  // но не входит в /dashboard». Проблема: после loginByToken parent сразу
  // делал navigate("/dashboard"), но React-state setUser(u) ещё не flushed
  // → dashboard рендерится с user=null → auth-guard срабатывал и
  // редиректил на /login. Решение:
  //   1. Гард на пустой token (если backend вернул verified без token —
  //      возвращаем null, parent покажет toast и не редиректит).
  //   2. Включаем isLoading=true на время fetch /api/auth/me. Dashboard
  //      auth-guard ждёт `!authLoading && !user`, поэтому пока isLoading
  //      true — не редиректит, показывает loader.
  //   3. Diagnostic console.log на ключевых точках flow для отладки в
  //      DevTools у Босса.
  const loginByToken = useCallback(async (newToken: string, remember = true) => {
    if (!newToken || typeof newToken !== "string") {
      // eslint-disable-next-line no-console
      console.warn("[AUTH] loginByToken: empty token, aborting");
      return null;
    }
    // eslint-disable-next-line no-console
    console.log("[AUTH] loginByToken: start, token len:", newToken.length);
    setIsLoading(true);
    setAuthState(newToken, null, remember);
    try {
      const res = await fetch("/api/auth/me", { headers: { Authorization: `Bearer ${newToken}` } });
      if (res.ok) {
        const j = await res.json();
        // Backend /api/auth/me возвращает user объект напрямую (см.
        // routes.ts:1088 — res.json(publicUser), без wrapper). Но на случай
        // если когда-то завернётся в {data: user} — оба варианта учитываем.
        const u = (j && typeof j === "object" && "id" in j) ? j : (j?.data || null);
        if (u && (u as any).id) {
          // eslint-disable-next-line no-console
          console.log("[AUTH] loginByToken: success, user id:", (u as any).id);
          setAuthState(newToken, u as PublicUser, remember);
          setIsLoading(false);
          return u as PublicUser;
        }
        // eslint-disable-next-line no-console
        console.warn("[AUTH] loginByToken: /api/auth/me returned no user payload", j);
      } else {
        // eslint-disable-next-line no-console
        console.warn("[AUTH] loginByToken: /api/auth/me HTTP", res.status);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[AUTH] loginByToken: network error", e);
    }
    // Не удалось — откатываем token (cookie + globalToken). Это
    // важно: иначе следующая навигация на /dashboard увидит token в
    // cookie, AuthProvider useEffect снова дёрнет /api/auth/me и снова
    // упадёт — юзер залипнет.
    setAuthState(null, null, false);
    setIsLoading(false);
    return null;
  }, [setAuthState]);

  const logout = useCallback(async () => {
    try {
      await apiRequest("POST", "/api/auth/logout");
    } catch {}
    setAuthState(null, null);
  }, [setAuthState]);

  return (
    <AuthContext.Provider value={{ user, token, isLoading, login, register, verifyRegister, loginByToken, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
